import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { useForm } from 'react-hook-form';
import { FormContainer, TextFieldElement } from 'react-hook-form-mui';
import {
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Stack,
} from '@mui/material';

import { EditButtons, SubstitutionIdInput } from '@barboxx/admin-components';
import { SUBSTITUTION_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { Substitution } from '@barboxx/models';
import { Can, useBool } from '@barboxx/react-utils';

interface Props {
    substitution: Substitution.DetailsResponse;
}

export const SubstitutionDetailsCard = ({ substitution }: Props) => {
    const [editable, editableActions] = useBool(false);
    const notifications = useNotifications();

    const formContext = useForm<Substitution.UpdateRequest>({
        resolver: yupResolver(SUBSTITUTION_SCHEMA),
        defaultValues: substitution,
    });

    const mutateUser = useMutation({
        mutationKey: ['substitution', substitution.id],
        mutationFn: (data: Substitution.UpdateRequest) =>
            Api.Core.Substitution.putSubstitution(substitution.id, data),
    });

    const handleCancel = () => {
        editableActions.off();
        formContext.reset(undefined);
    };

    const onSubmit = async (values: Substitution.UpdateRequest) => {
        return mutateUser
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show('Updated substitution!', {
                    severity: 'success',
                });

                editableActions.off();
                formContext.reset(data);
            })
            .catch(e => {
                notifications.show('Error Updating substitution!', {
                    severity: 'error',
                });
            });
    };

    return (
        <FormContainer onSuccess={onSubmit} formContext={formContext}>
            <Card>
                <CardHeader title="Substitution Details" />
                <CardContent>
                    <Stack gap={2}>
                        <TextFieldElement
                            fullWidth
                            name="name"
                            disabled={!editable}
                            label="Name"
                        />
                        <TextFieldElement
                            fullWidth
                            name="description"
                            disabled={!editable}
                            label="Description"
                        />
                        <SubstitutionIdInput
                            type="target"
                            disabled={!editable}
                        />

                        <SubstitutionIdInput
                            type="substitution"
                            disabled={!editable}
                        />
                    </Stack>
                </CardContent>
                <CardActions
                    sx={{
                        justifyContent: 'flex-end',
                        paddingRight: 3,
                        paddingBottom: 3,
                    }}
                >
                    <Can I="update" a="Substitution">
                        <EditButtons
                            formContext={formContext}
                            handleCancel={handleCancel}
                            editable={editable}
                            editableActions={editableActions}
                        />
                    </Can>
                </CardActions>
            </Card>
        </FormContainer>
    );
};
